import { List } from "immutable";
import moment from "moment";

// @ts-ignore
import {
  aggregateModuleStats,
  calculateCappedSessionDuration
} from "@seneca/session-aggregation";
import { ModuleStatsType } from "@seneca/session-aggregation/out/aggregateModuleStats/aggregateModuleStats";
import { ModuleStatI } from "@seneca/session-aggregation/out/types/Session";

import { ModuleRegistry } from "seneca-common/features/module/registry";
import {
  getSuggestionReason,
  getSuggestionReasonWeighting
} from "seneca-common/features/session/selectors/state/sessions/session/smartLearningSuggestionReason";
import { ModuleStudiedEventType } from "seneca-common/features/stats/state";
import { isSignedIn } from "seneca-common/features/user/state";

import isSmartLearningActive from "features/classroom/selectors/isSmartLearningActive";
import { getAppName, getAppVersion } from "features/common/utils/functions/app";

import SessionFinishedEvent from "../models/SessionFinishedEvent";
import SessionReport from "../models/SessionReport";
import { generateModuleStudiedEvent } from "./generateModuleStudiedEvent";
import {
  calculateSessionScore,
  getSessionCompleted,
  getSessionContentIds,
  getSessionCourseId,
  getSessionEndingCourseProficiency,
  getSessionEndingCourseScore,
  getSessionEndingProficiency,
  getSessionEndTime,
  getSessionIds,
  getSessionModuleIds,
  getSessionSectionIds,
  getSessionSmartLearningSuggestionReason,
  getSessionStartingCourseProficiency,
  getSessionStartingProficiency,
  getSessionStartTime,
  getSessionType,
  getWrongSessionModuleIds
} from "./session";

export function generateSessionReportsForAllFinishedSessions(
  state: any,
  moduleRegistry: ModuleRegistry
): List<SessionReport> {
  return getSessionIds(state)
    .filter(sessionId => getSessionCompleted(state, sessionId))
    .sort((sessionId1, sessionId2) =>
      moment(getSessionEndTime(state, sessionId1)!).diff(
        getSessionEndTime(state, sessionId2)!,
        "seconds"
      )
    )
    .reduce((reports, sessionId) => {
      return reports.push(
        generateSessionReport(state, sessionId, moduleRegistry)
      );
    }, List());
}

export function generateTotalStudyTimeFromModules(
  state: any,
  sessionId: string,
  moduleRegistry: ModuleRegistry
): number {
  const modules = getSessionModuleIds(state, sessionId).map(sessionModuleId =>
    generateModuleStudiedEvent(
      state,
      sessionId,
      sessionModuleId!,
      moduleRegistry
    )
  );

  return calculateCappedSessionDuration(modules.toJS() as ModuleStatI[]) * 1000;
}

export function generateSessionReport(
  state: any,
  sessionId: string,
  moduleRegistry: ModuleRegistry
): SessionReport {
  const modules = getSessionModuleIds(state, sessionId).map(sessionModuleId =>
    generateModuleStudiedEvent(
      state,
      sessionId,
      sessionModuleId!,
      moduleRegistry
    )
  );

  return new SessionReport({
    platform: getAppName(),
    clientVersion: getAppVersion(),
    session: generateSessionFinishedEvent(state, sessionId, modules),
    modules
  });
}

export function generateSessionFinishedEvent(
  state: any,
  sessionId: string,
  modules: List<ModuleStudiedEventType>
): SessionFinishedEvent {
  const courseId = getSessionCourseId(state, sessionId);
  const sectionIds = getSessionSectionIds(state, sessionId);
  const smartLearningActive = isSmartLearningActive(state);
  const suggestedReason = smartLearningActive
    ? getSessionSmartLearningSuggestionReason(state, sessionId)
    : undefined;

  return new SessionFinishedEvent({
    sessionId,
    courseId,
    sectionIds,
    contentIds: getSessionContentIds(state, sessionId),
    ...aggregateModuleStats(modules.toJS() as ModuleStatsType[]),
    timeStarted: getSessionStartTime(state, sessionId),
    timeFinished: getSessionEndTime(state, sessionId),
    startingProficiency: getSessionStartingProficiency(state, sessionId),
    endingProficiency: getSessionEndingProficiency(state, sessionId),
    startingCourseProficiency: getSessionStartingCourseProficiency(
      state,
      sessionId
    ),
    endingCourseProficiency: getSessionEndingCourseProficiency(
      state,
      sessionId
    ),
    endingCourseScore: getSessionEndingCourseScore(state, sessionId),
    sessionScore: calculateSessionScore(state, sessionId),
    completed: getSessionCompleted(state, sessionId),
    smartLearningActive,
    topSmartLearningSuggestionRank:
      suggestedReason && getSuggestionReasonWeighting(suggestedReason),
    topSmartLearningSuggestionReason:
      suggestedReason && getSuggestionReason(suggestedReason),
    signedOut: !isSignedIn(state),
    sessionType: getSessionType(state, sessionId),
    wrongModuleIds: getWrongSessionModuleIds(state, sessionId)
  });
}
